/**
 * Client mirror of `src/lib/events.ts` (root) — keep the two in sync.
 * Web's tsconfig only compiles `web/src/**`, so we can't import the root
 * helper directly. Same pure logic: an event is "live now" as long as we
 * haven't passed its end, where end resolves from (in priority):
 *   1. event.endDate (organiser knows best)
 *   2. venue closingTimes[day] (venue-local HH:MM; earlier hour = next day)
 *   3. event.date + 4h fallback
 */

import type { VenueClosingTimes } from "./types";

export const DAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"] as const;

type LiveEventInput = { date: string | Date; endDate?: string | Date | null };

const HHMM_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

function parseHHMM(v: unknown): { h: number; m: number } | null {
  if (typeof v !== "string") return null;
  const match = HHMM_RE.exec(v);
  if (!match) return null;
  return { h: Number(match[1]), m: Number(match[2]) };
}

type LocalParts = { year: number; month: number; day: number; hour: number; minute: number; dayOfWeek: number };

function getLocalDateParts(d: Date, timeZone: string): LocalParts {
  const fmt = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    weekday: "short",
    hour12: false,
  });
  const parts = fmt.formatToParts(d).reduce(
    (acc, p) => ({ ...acc, [p.type]: p.value }),
    {} as Record<string, string>,
  );
  const dayMap: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    hour: Number(parts.hour === "24" ? "0" : parts.hour),
    minute: Number(parts.minute),
    dayOfWeek: dayMap[parts.weekday] ?? 0,
  };
}

function setLocalTime(base: Date, hour: number, minute: number, timeZone: string): Date {
  const target = getLocalDateParts(base, timeZone);
  let guess = new Date(Date.UTC(target.year, target.month - 1, target.day, hour, minute, 0));
  for (let i = 0; i < 3; i++) {
    const local = getLocalDateParts(guess, timeZone);
    const localMs = Date.UTC(local.year, local.month - 1, local.day, local.hour, local.minute, 0);
    const wantedMs = Date.UTC(target.year, target.month - 1, target.day, hour, minute, 0);
    const diff = wantedMs - localMs;
    if (diff === 0) return guess;
    guess = new Date(guess.getTime() + diff);
  }
  return guess;
}

function addLocalDays(base: Date, days: number, timeZone: string): Date {
  const parts = getLocalDateParts(base, timeZone);
  return setLocalTime(
    new Date(Date.UTC(parts.year, parts.month - 1, parts.day + days, parts.hour, parts.minute, 0)),
    parts.hour,
    parts.minute,
    timeZone,
  );
}

export function resolveEventEnd(
  event: LiveEventInput,
  closingTimes?: VenueClosingTimes,
  timeZone = "Europe/London",
): Date {
  const start = new Date(event.date);
  if (event.endDate) {
    const end = new Date(event.endDate);
    if (end.getTime() > start.getTime()) return end;
  }
  const local = getLocalDateParts(start, timeZone);
  const dayKey = DAY_KEYS[local.dayOfWeek];
  const closing = closingTimes?.[dayKey];
  const parsed = closing ? parseHHMM(closing) : null;
  if (!parsed) return new Date(start.getTime() + 4 * 60 * 60_000);

  const closesNextDay =
    parsed.h < local.hour || (parsed.h === local.hour && parsed.m <= local.minute);
  const closeDate = closesNextDay ? addLocalDays(start, 1, timeZone) : start;
  return setLocalTime(closeDate, parsed.h, parsed.m, timeZone);
}

export function isEventLive(
  event: LiveEventInput,
  closingTimes?: VenueClosingTimes,
  now: Date = new Date(),
  timeZone = "Europe/London",
): boolean {
  const start = new Date(event.date);
  if (now.getTime() < start.getTime()) return false;
  return now.getTime() < resolveEventEnd(event, closingTimes, timeZone).getTime();
}

/** Short human string like "ends 4:00 AM" for the hero card. */
export function formatEndTime(
  event: LiveEventInput,
  closingTimes?: VenueClosingTimes,
  timeZone = "Europe/London",
): string {
  const end = resolveEventEnd(event, closingTimes, timeZone);
  return new Intl.DateTimeFormat("en-GB", {
    timeZone,
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(end);
}
